import React, { useState, useRef, useEffect } from "react";
import { cx } from "../../Util/classHelpers";
import { TIMEFRAME_OPTIONS } from "../ChAnalytics/chMetricConfig";
import { ChLocationIcon, ChChevronIcon, ChSearchIcon, ChCalendarIcon } from "../Icons/ch-analytics-icons";
import DateRangePicker from "../CommonComponents/DateRangePicker";
import styles from "./chHeatmap.module.css";

const ChHeatmapFilters = ({
    companyList, selectedLocations, onLocationsChange,
    timeframe, onTimeframeChange,
    dateRange, onRangeChange, onRangeClear,
}) => {
    const isCustomRange = timeframe === "range" && dateRange.start && dateRange.end;

    const subtitleLabel = isCustomRange
        ? `${fmtDate(new Date(dateRange.start + "T00:00:00"))} – ${fmtDate(new Date(dateRange.end + "T00:00:00"))}`
        : getDateRangeLabel(timeframe);

    return (
        <div>
            <div className={styles.filterRow}>
                <LocationFilter
                    companyList={companyList}
                    selectedLocations={selectedLocations}
                    onLocationsChange={onLocationsChange}
                />
                <TimeFrameSelector
                    timeframe={timeframe}
                    onTimeframeChange={onTimeframeChange}
                />
                <DateRangePicker
                    value={dateRange}
                    onChange={onRangeChange}
                    onClear={onRangeClear}
                    maxRangeDays={90}
                />
            </div>
            {subtitleLabel && (
                <div className={cx(styles.rangeSubtitle, isCustomRange && styles.rangeSubtitleActive)}>
                    <ChCalendarIcon />
                    <span>Showing: {subtitleLabel}</span>
                </div>
            )}
        </div>
    );
};

const LocationFilter = ({ companyList, selectedLocations, onLocationsChange }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        function handler(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const filtered = companyList.filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase())
    );

    const isAll = companyList.length > 0 && selectedLocations.length === companyList.length;

    function toggleAll() {
        if (isAll) {
            onLocationsChange([]);
        } else {
            onLocationsChange(companyList.map((c) => c.id));
        }
    }

    function toggleCompany(id) {
        if (selectedLocations.includes(id)) {
            onLocationsChange(selectedLocations.filter((x) => x !== id));
        } else {
            onLocationsChange([...selectedLocations, id]);
        }
    }

    const label = isAll
        ? "All Locations"
        : selectedLocations.length === 0
        ? "Select Locations"
        : selectedLocations.length === 1
        ? companyList.find((c) => c.id === selectedLocations[0])?.name ?? "1 location"
        : `${selectedLocations.length} locations selected`;

    return (
        <div className={styles.locWrap} ref={ref}>
            <button
                className={cx(styles.locBtn, open && styles.locBtnOpen)}
                onClick={() => setOpen((v) => !v)}
            >
                <ChLocationIcon />
                <span>{label}</span>
                <ChChevronIcon />
            </button>

            {open && (
                <div className={styles.locDropdown}>
                    <div className={styles.locSearch}>
                        <ChSearchIcon />
                        <input
                            placeholder="Search by store name"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onMouseDown={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div className={styles.locList}>
                        {!search && (
                            <label className={styles.locItem}>
                                <input
                                    type="checkbox"
                                    checked={isAll}
                                    onChange={toggleAll}
                                />
                                <span>Aggregate for all stores</span>
                            </label>
                        )}
                        {filtered.map((c) => (
                            <label key={c.id} className={styles.locItem}>
                                <input
                                    type="checkbox"
                                    checked={selectedLocations.includes(c.id)}
                                    onChange={() => toggleCompany(c.id)}
                                />
                                <span>{c.name}</span>
                            </label>
                        ))}
                        {filtered.length === 0 && (
                            <div className={styles.locEmpty}>No stores match</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const TimeFrameSelector = ({ timeframe, onTimeframeChange }) => {
    return (
        <div className={styles.tfTabs}>
            {TIMEFRAME_OPTIONS.map(({ label, value }) => (
                <button
                    key={value}
                    className={cx(styles.tfTab, timeframe === value && styles.tfTabActive)}
                    onClick={() => onTimeframeChange(value)}
                    title={getDateRangeLabel(value)}
                >
                    {label}
                </button>
            ))}
        </div>
    );
};

const fmtDate = (d) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

function getDateRangeLabel(value) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (value) {
        case "today":
            return fmtDate(today);
        case "yesterday": {
            const d = new Date(today);
            d.setDate(d.getDate() - 1);
            return fmtDate(d);
        }
        case "thisWeek": {
            const day = today.getDay();
            const mon = new Date(today);
            mon.setDate(mon.getDate() - ((day === 0 ? 7 : day) - 1));
            return `${fmtDate(mon)} – ${fmtDate(today)}`;
        }
        case "lastWeek": {
            const day = today.getDay();
            const thisMon = new Date(today);
            thisMon.setDate(thisMon.getDate() - ((day === 0 ? 7 : day) - 1));
            const lastMon = new Date(thisMon);
            lastMon.setDate(lastMon.getDate() - 7);
            const lastSun = new Date(lastMon);
            lastSun.setDate(lastSun.getDate() + 6);
            return `${fmtDate(lastMon)} – ${fmtDate(lastSun)}`;
        }
        case "thisMonth": {
            const first = new Date(today.getFullYear(), today.getMonth(), 1);
            return `${fmtDate(first)} – ${fmtDate(today)}`;
        }
        case "lastMonth": {
            const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const last = new Date(today.getFullYear(), today.getMonth(), 0);
            return `${fmtDate(first)} – ${fmtDate(last)}`;
        }
        default:
            return "";
    }
}

export default ChHeatmapFilters;
